import { useState } from "react";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import t from "@/lang/lang";
import { MeasurementTemplate } from "@/services/backend/samples/types/sample-base-data";
import type { WaterKind } from "@/services/backend/samples/types/water-sample";
import { InputDebounced } from "@/shared/components/inputs/input-debounced";
import { H4 } from "@/shared/components/typography";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { TableCard } from "@/shared/components/tables/table-card";

type TemplateValues = Pick<
  MeasurementTemplate,
  "name" | "referenceValue" | "unit"
>;

/**
 * Edits the measurement templates of water base data: one ordered list per
 * water kind.
 *
 * It holds no copy of the templates. Every change reports the complete new
 * list, since the backend replaces all templates with each patch. The order
 * of a template is derived from its position within its water kind
 * (1-based), so it is always unique per kind.
 */
export function WaterBaseDataMeasurementsEditor({
  measurements,
  onMeasurementsChange,
}: {
  measurements: MeasurementTemplate[];
  onMeasurementsChange: (measurements: MeasurementTemplate[]) => void;
}) {
  const cold = templatesOf(measurements, "cold");
  const warm = templatesOf(measurements, "warm");

  return (
    <div className="grid gap-6">
      <MeasurementTemplateSection
        title={t("Kaltwasser")}
        waterKind="cold"
        templates={cold}
        onTemplatesChange={(next) =>
          onMeasurementsChange([...withOrder(next), ...warm])
        }
      />
      <MeasurementTemplateSection
        title={t("Warmwasser")}
        waterKind="warm"
        templates={warm}
        onTemplatesChange={(next) =>
          onMeasurementsChange([...cold, ...withOrder(next)])
        }
      />
    </div>
  );
}

/** Returns the templates of the water kind in their order. */
function templatesOf(
  measurements: MeasurementTemplate[],
  waterKind: WaterKind,
): MeasurementTemplate[] {
  return measurements
    .filter((m) => m.waterKind === waterKind)
    .toSorted((a, b) => a.order - b.order);
}

/** Numbers the templates by their position, starting at 1. */
function withOrder(templates: MeasurementTemplate[]): MeasurementTemplate[] {
  return templates.map((m, i) => ({ ...m, order: i + 1 }));
}

/** Returns the templates with the one at `from` moved to `to`. */
function move<T>(items: T[], from: number, to: number): T[] {
  const next = [...items];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

function MeasurementTemplateSection({
  title,
  waterKind,
  templates,
  onTemplatesChange,
}: {
  title: string;
  waterKind: WaterKind;
  templates: MeasurementTemplate[];
  onTemplatesChange: (templates: MeasurementTemplate[]) => void;
}) {
  const update = (index: number, change: Partial<TemplateValues>) =>
    onTemplatesChange(
      templates.map((m, i) => (i === index ? { ...m, ...change } : m)),
    );

  return (
    <section className="grid gap-2">
      <H4>{title}</H4>
      <TableCard>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">{t("Nr.")}</TableHead>
              <TableHead>{t("Bezeichnung")}</TableHead>
              <TableHead>{t("Referenzwert")}</TableHead>
              <TableHead>{t("Einheit")}</TableHead>
              <TableHead className="w-36">{t("Aktionen")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {templates.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="text-muted-foreground text-center"
                >
                  {t("Keine Messungen vorhanden.")}
                </TableCell>
              </TableRow>
            )}
            {templates.map((template, index) => (
              // Rows have no identity besides their position. The debounced
              // inputs re-sync their value when a row moves or is removed.
              <TableRow key={index}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                  <InputDebounced
                    aria-label={t("Bezeichnung")}
                    value={template.name}
                    onChange={(e) => update(index, { name: e.target.value })}
                  />
                </TableCell>
                <TableCell>
                  <InputDebounced
                    aria-label={t("Referenzwert")}
                    value={template.referenceValue}
                    onChange={(e) =>
                      update(index, { referenceValue: e.target.value })
                    }
                  />
                </TableCell>
                <TableCell>
                  <InputDebounced
                    aria-label={t("Einheit")}
                    value={template.unit}
                    onChange={(e) => update(index, { unit: e.target.value })}
                  />
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={t("Nach oben")}
                      disabled={index === 0}
                      onClick={() =>
                        onTemplatesChange(move(templates, index, index - 1))
                      }
                    >
                      <ArrowUp />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={t("Nach unten")}
                      disabled={index === templates.length - 1}
                      onClick={() =>
                        onTemplatesChange(move(templates, index, index + 1))
                      }
                    >
                      <ArrowDown />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={t("Entfernen")}
                      onClick={() =>
                        onTemplatesChange(
                          templates.filter((_, i) => i !== index),
                        )
                      }
                    >
                      <Trash2 />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            <AddMeasurementTemplateRow
              onAdd={(values) =>
                onTemplatesChange([
                  ...templates,
                  { ...values, waterKind, order: templates.length + 1 },
                ])
              }
            />
          </TableBody>
        </Table>
      </TableCard>
    </section>
  );
}

const emptyTemplate: TemplateValues = {
  name: "",
  referenceValue: "",
  unit: "",
};

/**
 * Collects a new template before it is added. The backend rejects a
 * template without a name, so it is only added — and saved — once it has
 * one. The draft is not part of the base data, hence the local state.
 */
function AddMeasurementTemplateRow({
  onAdd,
}: {
  onAdd: (values: TemplateValues) => void;
}) {
  const [draft, setDraft] = useState(emptyTemplate);
  const canAdd = draft.name.trim() !== "";

  const add = () => {
    if (!canAdd) {
      return;
    }
    onAdd({ ...draft, name: draft.name.trim() });
    setDraft(emptyTemplate);
  };

  return (
    <TableRow>
      <td aria-hidden="true" />
      <TableCell>
        <Input
          aria-label={t("Bezeichnung der neuen Messung")}
          placeholder={t("Neue Messung")}
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              add();
            }
          }}
        />
      </TableCell>
      <TableCell>
        <Input
          aria-label={t("Referenzwert der neuen Messung")}
          value={draft.referenceValue}
          onChange={(e) =>
            setDraft({ ...draft, referenceValue: e.target.value })
          }
        />
      </TableCell>
      <TableCell>
        <Input
          aria-label={t("Einheit der neuen Messung")}
          value={draft.unit}
          onChange={(e) => setDraft({ ...draft, unit: e.target.value })}
        />
      </TableCell>
      <TableCell>
        <Button variant="outline" size="sm" disabled={!canAdd} onClick={add}>
          <Plus />
          <span>{t("Hinzufügen")}</span>
        </Button>
      </TableCell>
    </TableRow>
  );
}
